import { mapGetters } from 'vuex';
import { InventoryFormFormGetters } from '../../store/getters';
import { InventoryFormAction } from '../../enums/inventoryFormAction';
import { InventoryFormOperator } from '../../enums/inventoryFormOperator';
import gridViewFieldsUtils from '../../utils/gridViewFieldsUtils';

export default {
	computed: {
		...mapGetters({
			interactions: [InventoryFormFormGetters.INTERACTIONS],
			selectedInventoryForm: [
				InventoryFormFormGetters.SELECTED_INVENTORY_FORM,
			],
		}),
		isVisible() {
			if (!this.formField) return false;
			if (
				this.formField.HiddenBehaviour > 0 &&
				this.formField.OnScanEdit === 0
			)
				return false;
			return this.visible;
		},
	},
	data() {
		return {
			visible: true,
			visibleBubble: false,
			bubbleText: '',
			visibleHelp: false,
			helpText: '',
			set: true,
			setValue: null,
			questionHelp: false,
			validationError: null,
		};
	},
	watch: {
		record() {
			// Check global saving flag first
			if (this.isSaving) {
				return; // Skip all interaction updates during save
			}

			if (this.skipNextRecordUpdate) {
				this.skipNextRecordUpdate = false;
				return;
			}
			this.udateFieldsBasedOnInteraction();
		},
	},
	methods: {
		udateFieldsBasedOnInteraction() {
			this.handleVisibility();
			this.handleBubble();
			this.handleHelp();

			this.handleSet();
			this.handleClear();
			this.handleQuestionHelp();
			this.handleValidate();
		},
		handleVisibility() {
			let showInteractions = this.getInteractionByActionValue(
				InventoryFormAction.Show
			);
			let hideInteractions = this.getInteractionByActionValue(
				InventoryFormAction.Hide
			);

			// No visibility interactions target this field: keep its default.
			if (showInteractions.length === 0 && hideInteractions.length === 0)
				return;

			// Start from the field's original/default visibility so it resets
			// once none of the interaction conditions are met.
			let visible = this.formField.IsVisible;

			// A met Show condition forces the field visible.
			if (
				showInteractions.some((i) =>
					this.validateConditions(i.Conditions)
				)
			)
				visible = true;

			// A met Hide condition forces the field hidden and always wins.
			if (
				hideInteractions.some((i) =>
					this.validateConditions(i.Conditions)
				)
			)
				visible = false;

			this.visible = visible;
		},
		handleBubble() {
			let showInteractions = this.getInteractionByActionValue(
				InventoryFormAction.ShowBubble
			);
			let hideInteractions = this.getInteractionByActionValue(
				InventoryFormAction.HideBubble
			);

			if (showInteractions.length === 0 && hideInteractions.length === 0)
				return;

			// Accumulate bubble text only from Show interactions whose
			// conditions are actually met.
			let text = '';
			let anyShowMet = false;
			showInteractions.forEach((i) => {
				if (!this.validateConditions(i.Conditions)) return;
				anyShowMet = true;
				i.Actions.filter(
					(a) =>
						a.FormFieldId === this.formField.Id &&
						a.Value === InventoryFormAction.ShowBubble
				).forEach((a) => {
					if (a.Comment) text += a.Comment + ' ';
				});
			});

			let anyHideMet = hideInteractions.some((i) =>
				this.validateConditions(i.Conditions)
			);

			let visible = showInteractions.length > 0 ? anyShowMet : false;
			if (anyHideMet) visible = false;

			this.visibleBubble = visible;
			this.bubbleText = visible ? text.trim() : '';
		},
		handleHelp() {
			let showInteractions = this.getInteractionByActionValue(
				InventoryFormAction.ShowHelp
			);
			let hideInteractions = this.getInteractionByActionValue(
				InventoryFormAction.HideHelp
			);

			if (showInteractions.length === 0 && hideInteractions.length === 0)
				return;

			let text = '';
			let anyShowMet = false;
			showInteractions.forEach((i) => {
				if (!this.validateConditions(i.Conditions)) return;
				anyShowMet = true;
				i.Actions.filter(
					(a) =>
						a.FormFieldId === this.formField.Id &&
						a.Value === InventoryFormAction.ShowHelp
				).forEach((a) => {
					if (a.Comment) text += a.Comment + ' ';
				});
			});

			let anyHideMet = hideInteractions.some((i) =>
				this.validateConditions(i.Conditions)
			);

			let visible = showInteractions.length > 0 ? anyShowMet : false;
			if (anyHideMet) visible = false;

			this.visibleHelp = visible;
			this.helpText = visible ? text.trim() : '';
		},
		handleSet() {
			let interactions = this.getInteractionByActionValue(
				InventoryFormAction.Set
			);

			// Reset state
			let shouldSet = false;
			let valueToSet = null;

			interactions.forEach((i) => {
				// Find actions targeting this field
				var relatedActions = i.Actions.filter(
					(a) =>
						a.FormFieldId === this.formField.Id &&
						a.Value === InventoryFormAction.Set
				);

				// Check if conditions are met
				if (
					relatedActions.length > 0 &&
					this.validateConditions(i.Conditions)
				) {
					// Get the value from the action's Comment field
					relatedActions.forEach((a) => {
						valueToSet = a.Comment;
					});

					if (this.selectedValue != valueToSet) {
						shouldSet = true;
					}
				}
			});

			// Apply the result by directly setting selectedValue
			if (shouldSet) {
				this.onSetInteraction(valueToSet);
			}
		},
		handleClear() {
			let interactions = this.getInteractionByActionValue(
				InventoryFormAction.Clear
			);

			// Reset state
			let shouldSet = false;
			interactions.forEach((i) => {
				// Find actions targeting this field
				var relatedActions = i.Actions.filter(
					(a) =>
						a.FormFieldId === this.formField.Id &&
						a.Value === InventoryFormAction.Clear
				);

				// Check if conditions are met
				if (
					relatedActions.length > 0 &&
					this.validateConditions(i.Conditions)
				) {
					if (this.selectedValue != null) {
						shouldSet = true;
					}
				}
			});

			// Apply the result by directly setting selectedValue
			if (shouldSet) {
				this.onClearInteraction();
			}
		},
		handleQuestionHelp() {
			let interactions = this.getInteractionByActionValue(
				InventoryFormAction.QuestionHelp
			);
			if (interactions.length === 0) return;
			this.questionHelp = interactions.some((i) =>
				this.validateConditions(i.Conditions)
			);
		},
		handleValidate() {
			let interactions = this.getInteractionByActionValue(
				InventoryFormAction.Validate
			);

			this.validationError = null; // Reset error

			interactions.forEach((i) => {
				// Find actions targeting this field
				var relatedActions = i.Actions.filter(
					(a) =>
						a.FormFieldId === this.formField.Id &&
						a.Value === InventoryFormAction.Validate
				)

				// Check if conditions are met
				if (
					relatedActions.length > 0 &&
					this.validateConditions(i.Conditions)
				) {
					// Get the regex from the action's Comment field
					relatedActions.forEach((a) => {
						let regInstance = a.Comment;

						if (typeof a.Comment === 'string') {
							const match = a.Comment.match(/^\/(.*)\/([a-z]*)$/);
							if (match) {
								regInstance = new RegExp(match[1], match[2]);
							} else {
								regInstance = new RegExp(a.Comment);
							}
						}

						if (this.selectedValue && !regInstance.test(this.selectedValue)) {
							this.validationError = 'Value does not match the required pattern.';
						}
					});
				}
			});
		},
		getInteractionByActionValue(value) {
			let result = [];
			this.interactions.forEach((i) => {
				var relatedActions = i.Actions.filter(
					(a) =>
						a.FormFieldId === this.formField.Id && a.Value === value
				);
				if (relatedActions.length > 0) result.push(i);
			});
			return result;
		},

		validateConditions(conditions) {
			let result = true;
			conditions.forEach((c) => {
				let field = this.selectedInventoryForm.FormFields.find(
					(f) => f.Id == c.FormFieldId
				);
				if (field) {
					let key = gridViewFieldsUtils.getFieldName(
						field.GridViewFields[0]
					);
					switch (c.Operator) {
						case InventoryFormOperator.IsAnything:
							if (this.record[key]) result = result && true;
							else result = result && false;
							break;
						case InventoryFormOperator.IsBlank:
							if (
								this.record[key] === undefined ||
								this.record[key] === null ||
								this.record[key].trim() === ''
							)
								result = result && true;
							else result = result && false;
							break;
						case InventoryFormOperator.Is:
							if (c.Value == this.record[key])
								result = result && true;
							else result = result && false;
							break;
						case InventoryFormOperator.IsNot:
							if (c.Value != this.record[key])
								result = result && true;
							else result = result && false;
							break;
						case InventoryFormOperator.IsNotOneOf: {
							let notOneOfValues = c.Value.split(',').map((v) =>
								v.trim().toLowerCase()
							);
							let notOneOfRecordValue = this.record[key]
								? this.record[key].toLowerCase()
								: '';
							if (!notOneOfValues.includes(notOneOfRecordValue))
								result = result && true;
							else result = result && false;
							break;
						}
						case InventoryFormOperator.IsOneOf: {
							let oneOfValues = c.Value.split(',').map((v) =>
								v.trim().toLowerCase()
							);
							let oneOfRecordValue = this.record[key]
								? this.record[key].toLowerCase()
								: '';
							if (oneOfValues.includes(oneOfRecordValue))
								result = result && true;
							else result = result && false;
							break;
						}
						case InventoryFormOperator.Contains:
							if (
								this.record[key] &&
								this.record[key].includes(c.Value)
							)
								result = result && true;
							else result = result && false;
							break;
						case InventoryFormOperator.DoesNotContain:
							if (
								!this.record[key] ||
								!this.record[key].includes(c.Value)
							)
								result = result && true;
							else result = result && false;
							break;
						case InventoryFormOperator.StartsWith:
							if (
								this.record[key] &&
								this.record[key].startsWith(c.Value)
							)
								result = result && true;
							else result = result && false;
							break;
					}
				}
			});
			return result;
		},
	},
};
